/**
 * <SEO> — drop-in per-page SEO helmet.
 *
 * Usage:
 *   <SEO
 *     title="Νίκος Παπαδόπουλος"
 *     description="Μεσοεπιθετικός #10 της ΛΕΥΤΕΡΙΑ FC"
 *     canonical="/player/nikos-papadopoulos--abc123"
 *     image="/api/og/player/abc123/card.png"
 *     type="profile"
 *     jsonLd={personJsonLd}
 *   />
 *
 * What it sets:
 *   • <title> + meta description
 *   • Open Graph (og:title / og:description / og:image / og:url / og:type)
 *   • Twitter Card
 *   • canonical link
 *   • Optional JSON-LD structured data block (Person / NewsArticle / SportsEvent / …)
 *
 * Falls back to club-wide defaults so even partially-configured pages get
 * something better than the static index.html meta. Already-shared links keep
 * working — this only AUGMENTS what crawlers see.
 */
import { Helmet } from "react-helmet-async";

const SITE_URL = (typeof window !== "undefined" ? window.location.origin : "");
const DEFAULT_TITLE = "ΛΕΥΤΕΡΙΑ FC | Λευτεριά";
const DEFAULT_DESC = "Ποδοσφαιρικος Συλλογος και Ακαδημια — Επισημη ιστοσελιδα της Λευτεριας FC.";
const DEFAULT_IMAGE = "/api/og/default.png";

export default function SEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ΛΕΥΤΕΡΙΑ FC` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const imgUrl = image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}${DEFAULT_IMAGE}`;
  const url = canonical
    ? (canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`)
    : SITE_URL;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content="ΛΕΥΤΕΡΙΑ FC" />
      <meta property="og:locale" content="el_GR" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imgUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imgUrl} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// ---- Helpers to build common JSON-LD payloads ----

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    name: "ΛΕΥΤΕΡΙΑ FC",
    alternateName: "Λευτεριά FC",
    url: SITE_URL || "https://lefteriafc.cy",
    logo: `${SITE_URL || "https://lefteriafc.cy"}/logo512.png`,
    sport: "Football",
    sameAs: [
      "https://www.facebook.com/lefteriafc",
      "https://www.instagram.com/lefteriafc",
    ].filter(Boolean),
  };
}

export function personJsonLd(player) {
  if (!player) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: player.name,
    image: player.image_url || undefined,
    jobTitle: player.position,
    nationality: player.nationality,
    affiliation: {
      "@type": "SportsTeam",
      name: player.team_type === "Academy" ? "ΛΕΥΤΕΡΙΑ FC Academy" : "ΛΕΥΤΕΡΙΑ FC",
    },
  };
}

export function newsArticleJsonLd(article) {
  if (!article) return null;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.excerpt || (article.content || "").slice(0, 200),
    image: article.image_url ? [article.image_url] : undefined,
    datePublished: article.created_at,
    dateModified: article.updated_at || article.created_at,
    author: {
      "@type": "Organization",
      name: "ΛΕΥΤΕΡΙΑ FC",
    },
    publisher: {
      "@type": "SportsOrganization",
      name: "ΛΕΥΤΕΡΙΑ FC",
      logo: { "@type": "ImageObject", url: `${SITE_URL || "https://lefteriafc.cy"}/logo512.png` },
    },
  };
}

export function sportsEventJsonLd(fixture) {
  if (!fixture) return null;
  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: `${fixture.home_team} vs ${fixture.away_team}`,
    startDate: fixture.match_date,
    sport: "Football",
    homeTeam: { "@type": "SportsTeam", name: fixture.home_team },
    awayTeam: { "@type": "SportsTeam", name: fixture.away_team },
    location: fixture.venue ? { "@type": "Place", name: fixture.venue } : undefined,
  };
}
